import { Injectable, computed, inject, signal } from "@angular/core";
import { Observable, tap, throwError } from "rxjs";
import { BaseApiService } from "./base.service";
import { IdentityService } from "./identity.service";
import { LanguageService } from "./language.service";
import { CHATBOT_CONFIG } from "../config";
import { domainToSignInto } from "../models/domain";
import { QueryResponse } from "../models/message";
import { AgentSummary } from "../models/agent";

@Injectable({ providedIn: "root" })
export class AgentsService extends BaseApiService {
    private readonly identity = inject(IdentityService);
    private readonly config = inject(CHATBOT_CONFIG);
    private readonly language = inject(LanguageService);

    protected readonly resourcePath = "agents";

    /**
     * The Agent Picker's override, `null` while talking to the domain's own agent. It is the
     * only state every domain had before the picker existed. Not persisted: it names one test conversation
     * for the lifetime of this tab, not a standing preference to resume on reload.
     */
    private readonly _selectedAgent = signal<string | null>(null);

    /**
     * Set alongside `_selectedAgent` when the caller must guarantee `isTestSession` regardless of
     * which agent it names, because the plain heuristic below is blind to the one case where the
     * agent being tried out *is* the domain's own, which would otherwise look identical to a real
     * conversation.
     */
    private readonly _forcedTestAgent = signal<string | null>(null);

    /** The agent the current domain's rules name, and null until a domain has been declared. */
    private readonly domainAgent = computed(() => this.identity.rules()?.agent ?? null);

    get agentName(): string | null {
        return this._selectedAgent() ?? this.domainAgent();
    }

    /**
     * A conversation is a test the instant it departs from what a real user of this domain would
     * be having: talking to an agent other than the domain's own, whether a sibling orchestrator
     * picked to try it out or a sub-agent addressed directly; holding the conversation as another
     * domain's user; or the caller forcing it through `selectAgent(name, { forceTest: true })`.
     *
     * Mirrors the rule the backend enforces on `isTest`, which it honours only for a caller that
     * can prove it may ask for an undeployed configuration to run.
     *
     * Picking the domain's own agent back out of the dropdown clears this the same way `resetAgent`
     * does: `_selectedAgent` is left non-null but equal to the default, which reads as "not a
     * test" here without a second state to keep in sync.
     */
    readonly isTestSession = computed(() => {
        if (this.identity.testDomain() !== null) {
            return true;
        }
        const selected = this._selectedAgent();
        if (selected === null) {
            return false;
        }
        return selected === this._forcedTestAgent() || selected !== this.domainAgent();
    });

    /**
     * Every agent the Agent Picker may offer, from the protected listing the agent configuration
     * screens already read. Reachable only with a credential that may administer agents, which is
     * also the only kind of domain whose rules offer the dropdown at all.
     */
    list(): Observable<AgentSummary[]> {
        return this._http.get<AgentSummary[]>(this._baseUrl);
    }

    /**
     * Switches to a different agent. The caller starts a fresh conversation to go with it.
     * `forceTest` is set only by a host that opens the widget already pointed at an agent, never
     * by the Agent Picker dropdown itself: picking the domain's own agent from the dropdown must
     * still read as "not a test".
     */
    selectAgent(name: string, options?: { forceTest?: boolean }): void {
        this._selectedAgent.set(name);
        this._forcedTestAgent.set(options?.forceTest ? name : null);
    }

    /**
     * The domain that has to be signed into before the current agent may be asked anything, and
     * null when the identity in use is already the right one.
     *
     * The rule this enforces: an agent that answers for a declared domain is only ever talked to
     * as one of that domain's users.
     */
    readonly signInRequired = computed(() => domainToSignInto(this.config.domains, this.agentName, this.identity.domain()));

    /** Back to the domain's own agent, as if the picker had never been touched. */
    resetAgent(): void {
        this._selectedAgent.set(null);
        this._forcedTestAgent.set(null);
    }

    /**
     * Sends a question to the current agent: the domain's own, or whichever the Agent Picker has
     * switched to.
     *
     * The free-query limit is deliberately not enforced here. Past the limit the agent keeps
     * answering, it just steers the conversation towards collecting what it needs first, so a
     * request refused by the client would cut off the very exchange that lifts the limit.
     * The identity headers are attached by `identityInterceptor`.
     */
    query(text: string): Observable<QueryResponse> {
        const agentName = this.agentName;
        if (!agentName) {
            return throwError(() => new Error("Chatbot: no agent to query. The host has not declared a domain yet."));
        }

        // The last line of defence for the rule, on the one path that actually sends: this agent
        // answers for a domain, and the identity asking is not one of its users.
        const missing = this.signInRequired();
        if (missing) {
            return throwError(() => new Error(`Chatbot: "${agentName}" answers for domain "${missing}", so it can only be asked as one of its users.`));
        }

        const body: Record<string, string | boolean> = {
            lang: this.language.currentLanguage(),
            text,
        };

        if (this.isTestSession()) {
            body["isTest"] = true;
        }

        // `userId` goes up whenever we have one, `clientToken` only alongside it.
        //
        // Not symmetric, and not an oversight. The backend refuses a clientToken without its userId,
        // but a userId on its own is exactly how a *new* conversation is filed under the *same* user:
        // it opens a fresh session and keeps the user it was given. Sending neither (which is what
        // "New chat" used to do, since `clearSession()` drops only the token) made the backend mint
        // a new user too, and `setSession` then overwrote the stored one. Every conversation ended
        // up under a user of its own, so the listing of a user's conversations could never return
        // more than the current one and the sidebar had no history to show.
        const clientToken = this.identity.clientToken;
        const userId = this.identity.userId;
        if (userId) {
            body["userId"] = userId;
            if (clientToken) {
                body["clientToken"] = clientToken;
            }
        }

        return this._http.post<QueryResponse>(`${this._baseUrl}/${agentName}/query`, body).pipe(
            tap((response) => {
                this.identity.setSession(response.clientToken, response.userId);
                this.identity.applyLeadState(response);
            }),
        );
    }
}
